import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { LOCALE_COOKIE_NAME } from '@/utils/locale-cookie';

/** Cookie Domain used on staging webshop hosts (legacy / scoped clears). */
export const WEBSHOP_DEV_E3CLOUD_COOKIE_DOMAIN = '.webshop.dev.e3cloud.io';

const isWebshopDevE3cloudHost = (hostname: string): boolean =>
  hostname === 'webshop.dev.e3cloud.io' || hostname.endsWith('.webshop.dev.e3cloud.io');

const eraseCookie = (
  name: string,
  opts: { domain: string; path: string; secure: boolean },
): void => {
  const base = `${name}=; path=${opts.path}; domain=${opts.domain}; max-age=0; SameSite=Lax`;
  document.cookie = opts.secure ? `${base}; Secure` : base;
};

/**
 * Removes `locale` scoped to `.webshop.dev.e3cloud.io` so stale cookies from an older
 * Domain attribute do not linger. Only runs when the current host is under that zone.
 */
export const clearLocaleCookieWebshopDevE3cloud = (): void => {
  if (typeof document === 'undefined' || typeof location === 'undefined') return;
  if (!isWebshopDevE3cloudHost(location.hostname)) return;

  eraseCookie(LOCALE_COOKIE_NAME, {
    domain: WEBSHOP_DEV_E3CLOUD_COOKIE_DOMAIN,
    path: '/',
    secure: location.protocol === 'https:',
  });
};

/** Add more page-load cleanup steps here (order matters). */
const PAGE_VISIT_CLEARING_SCRIPTS: Array<() => void> = [clearLocaleCookieWebshopDevE3cloud];

export const runPageVisitClearingScripts = (): void => {
  if (typeof window === 'undefined') return;
  for (const script of PAGE_VISIT_CLEARING_SCRIPTS) {
    script();
  }
};

/**
 * Runs cleanup when the app shell mounts and whenever the pathname changes (SPA navigations).
 * Keep scripts idempotent where possible.
 */
export const usePageVisitClearing = (): void => {
  const _pathname = usePathname();

  useEffect(() => {
    runPageVisitClearingScripts();
  }, []);
};
